import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../common/components/Input";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-speech-to-check stores keywords_info as
//   [{ word, idInternal, word_equivalent: { word1, word2, word3, word4 } }]
// on the content object (and, verbatim, in the PLD list_keyword_data column —
// the backend afterUpdate sync copies it without a per-element transform, so
// both sides share the same full shape). `word` + the four equivalents are all
// translatable text; `idInternal` is a server-managed stable identity. Unity
// matches the user's transcribed speech against `word` and the four equivalent
// slots. This bespoke editor keeps that shape and writes it straight back to
// react-hook-form so the existing save path round-trips it (content PUT +
// generic PLD keyMap map keywords_info -> list_keyword_data).

// minItems from the slopify jsonSchema (Emit 2-3, minimum 2). No hard maximum;
// existing data with more rows is preserved — we only guard the min on removal.
const MIN_KEYWORDS = 2;
const EQUIVALENT_SLOTS = ["word1", "word2", "word3", "word4"] as const;

interface KeywordEquivalent {
  word1: string;
  word2: string;
  word3: string;
  word4: string;
}
interface Keyword {
  word: string;
  idInternal: number | null;
  word_equivalent: KeywordEquivalent;
}

interface KeywordsInfoFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (add/remove) are source-language only. */
  isSourceLanguage: boolean;
  /** Optional per-word character cap. */
  maxLength?: number;
}

const emptyEquivalent = (): KeywordEquivalent => ({
  word1: "",
  word2: "",
  word3: "",
  word4: "",
});

const KeywordsInfoField = ({
  control,
  setValue,
  label,
  disabled = false,
  isSourceLanguage,
  maxLength,
}: KeywordsInfoFieldProps) => {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "keywords_info" });
  const keywords: Keyword[] = Array.isArray(watched) ? watched : [];

  // Text is editable in any language; structure is locked outside the source.
  const structureLocked = disabled || !isSourceLanguage;

  const write = (next: Keyword[]) => setValue("keywords_info", next, { shouldDirty: true });

  const handleWordChange = (index: number, value: string) => {
    const next = keywords.map((k, i) => (i === index ? { ...k, word: value } : k));
    write(next);
  };

  const handleEquivalentChange = (
    index: number,
    slot: (typeof EQUIVALENT_SLOTS)[number],
    value: string,
  ) => {
    const next = keywords.map((k, i) =>
      i === index
        ? {
            ...k,
            word_equivalent: { ...emptyEquivalent(), ...k.word_equivalent, [slot]: value },
          }
        : k,
    );
    write(next);
  };

  const handleAdd = () => {
    // Assign the next idInternal client-side (max + 1), mirroring the
    // server-side slopify post-process — a manual PUT does NOT auto-assign it,
    // and a null identity would break Unity's stable keyword mapping.
    const nextId = keywords.reduce((max, k) => Math.max(max, Number(k.idInternal) || 0), 0) + 1;
    write([...keywords, { word: "", idInternal: nextId, word_equivalent: emptyEquivalent() }]);
  };

  const handleRemove = (index: number) => {
    write(keywords.filter((_, i) => i !== index));
  };

  const canAdd = !structureLocked;
  const canRemove = !structureLocked && keywords.length > MIN_KEYWORDS;
  const atMinKeywords = keywords.length <= MIN_KEYWORDS;
  const showMinHint = !disabled && isSourceLanguage && atMinKeywords;
  const minHint = t("pages.project.editNode.keywordsMinHint") as string;

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          <span>{t("pages.project.editNode.keywordsAddKeyword")}</span>
        </button>
      </div>

      {!isSourceLanguage && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      )}

      {/* Reserved hint line (avoids layout shift) explaining the disabled remove
          buttons at the minimum. */}
      <p className="min-h-[1.25rem] mb-2 text-xs font-medium text-gray-500 italic">
        {showMinHint ? minHint : ""}
      </p>

      <div className="space-y-3">
        {keywords.map((keyword, index) => (
          <div key={keyword.idInternal ?? index} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <Input
                  type="text"
                  label={`${t("pages.project.editNode.keywordWord")} ${index + 1}`}
                  value={keyword.word ?? ""}
                  onChange={(e) => handleWordChange(index, e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  className="dynamic-field"
                />
              </div>
              <Tooltip
                message={!canRemove && !structureLocked ? minHint : ""}
                className="shrink-0 self-end pb-1"
                classNameChildren="right-0"
              >
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  disabled={!canRemove}
                  className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={t("general.delete") as string}
                >
                  <MdDeleteOutline size={20} />
                </button>
              </Tooltip>
            </div>

            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {EQUIVALENT_SLOTS.map((slot, slotIndex) => (
                <Input
                  key={slot}
                  type="text"
                  label={`${t("pages.project.editNode.keywordEquivalent")} ${slotIndex + 1}`}
                  value={keyword.word_equivalent?.[slot] ?? ""}
                  onChange={(e) => handleEquivalentChange(index, slot, e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  className="dynamic-field"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KeywordsInfoField;
